import { useEffect, useState } from "react";
import { Typography, Box, Chip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { ITurnover } from "../../../interface/turnover";
import { RootState } from "../../../redux/reducers";
import { setTotalByMonth } from "../../../redux/actions/forecastActions";
import getTurnoverData from "../../../tools/forecast/getTurnoverData";
import getForecastMonthList from "../../../tools/forecast/getForecastMonthList";
import CustomDataGrid from "../../customDataGrid/CustomDataGrid";

const TurnoverData = ({ 
    forecastVersion, forecastDates, type, agency, searchClient, searchDetail, 
    setSelectedTurnover, openModal, showDeleteDialog, setOpenModal, setSelectedData, selectedData, setEditId 
}: any) => {
    
    const dispatch = useDispatch();
    const params = useParams();
    const token = useSelector((state: RootState) => state.user.token);
    
    const [turnoverData, setTurnoverData] = useState<ITurnover[]>([]);
    const monthList = getForecastMonthList(forecastDates);

    useEffect(() => {
        getTurnoverData(params.id, forecastVersion, agency, type, setTurnoverData, token, dispatch, () => {});
    }, [agency, forecastVersion, openModal, showDeleteDialog]);

    // --- LOGIQUE DE FILTRAGE ---
    const filteredData = turnoverData.filter((t: ITurnover) => {
        const clientName = t.client?.name || "";
        const matchClient = clientName.toLowerCase().includes(searchClient.toLowerCase());
        
        let matchDetail = true;
        if (searchDetail) {
            const detailValue = t.type === "Régie" 
                ? (t.collab ? `${t.collab.firstname} ${t.collab.lastname}` : t.fictifCollab || "")
                : (t.project || "");
            matchDetail = detailValue.toLowerCase().includes(searchDetail.toLowerCase());
        }
        return matchClient && matchDetail;
    });

    // --- COLONNES ---
    const columns = [
        { field: "agency", headerName: "Agence" },
        { field: "client", headerName: "Client" },
        { field: type === "Régie" ? "collab" : "project", headerName: type === "Régie" ? "Collaborateur" : "Projet" },
        ...monthList.map((m: any) => ({ field: m.field, headerName: m.name, align: 'center' }))
    ];

    // --- LIGNES ---
    const turnoverRows = filteredData.map((t: ITurnover) => {
        const row: any = { id: t._id, agency: t.agency || "N/A", client: t.client.name };
        
        if (t.type === "Régie") {
            row["collab"] = t.collab ? `${t.collab.firstname} ${t.collab.lastname}` : t.fictifCollab;
            t.months.forEach(m => {
                row[m.label] = `
                    <div style="line-height: 1.2;">
                        <span style="font-size: 0.65rem; color: #94a3b8">JO: ${m.openDays} | ${m.tjm}€</span><br/>
                        <span style="font-weight: 700">${(m.openDays * m.tjm).toLocaleString()} €</span>
                    </div>`;
            });
        } else {
            row["project"] = t.project || "Non précisé";
            t.months.forEach(m => {
                row[m.label] = `<div style="font-weight: 700">${m.amount.toLocaleString()} €</div>`;
            });
        }
        return row;
    });

    // --- TOTAUX DYNAMIQUES ---
    const totalByMonth = monthList.reduce((acc: any, m: any) => {
        acc[m.field] = filteredData.reduce((sum, t: any) => {
            const match = t.months.find((tm: any) => tm.label === m.field);
            return sum + (match ? (t.type === "Régie" ? (match.openDays * match.tjm) : match.amount) : 0);
        }, 0);
        return acc;
    }, {});

    useEffect(() => {
        dispatch(setTotalByMonth(type, totalByMonth));
    }, [totalByMonth]);

    const totalRow = {
        id: "total",
        collab: `<span style="color: #f59e0b; font-weight: 900">TOTAL ${type.toUpperCase()}</span>`,
        project: `<span style="color: #f59e0b; font-weight: 900">TOTAL ${type.toUpperCase()}</span>`,
        ...Object.fromEntries(Object.entries(totalByMonth).map(([k, v]) => [
            k, `<span style="color: #f59e0b; font-weight: 900">${(v as number).toLocaleString()} €</span>`
        ]))
    };

    const rows = [...turnoverRows, totalRow];

    if (turnoverData.length === 0) return <></>;

    return (
        <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, px: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#64748b", textTransform: 'uppercase' }}>{type}</Typography>
                <Chip label={`${filteredData.length} prestations`} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: "#e2e8f0" }} />
            </Box>

            <CustomDataGrid 
                rows={rows} 
                columns={columns} 
                checkboxSelection 
                disableRowSelectionOnClick 
                rowSelectionModel={selectedData}
                onRowSelectionModelChange={(newSelection: any) => {
                    setSelectedData(newSelection);
                    setSelectedTurnover((prev: any) => ({ ...prev, [type]: newSelection }));
                }}
                isRowSelectable={({ row }: any) => row.id !== 'total'}
                onCellClick={({ row, field }: any) => {
                    if (row.id !== 'total' && field !== '__check__') {
                        setEditId(row.id);
                        setOpenModal(true);
                    }
                }}
                sx={{
                    bgcolor: 'white', borderRadius: '12px', border: "1px solid #e2e8f0", overflow: 'hidden',
                    "& .kairos-table-head": { bgcolor: "#f8fafc" }
                }}
            />
        </Box>
    );
};

export default TurnoverData;